import { useState } from 'react';

const QuoteForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    projectType: '',
    squareFootage: '',
    stoneType: '',
    notes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6hcdjg57726vudt7sg0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as Record<string, string>).toString()
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          phone: '',
          email: '',
          address: '',
          projectType: '',
          squareFootage: '',
          stoneType: '',
          notes: ''
        });
        setTimeout(() => setSubmitSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="quote-form" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold text-black mb-6">
            Request a Quote
          </h2>
          <p className="text-xl text-gray-600">
            Tell us about your stone project and we'll provide a detailed estimate.
          </p>
        </div>

        {submitSuccess && (
          <div className="mb-8 p-6 bg-green-50 border-2 border-green-500 rounded-lg">
            <p className="text-green-800 font-bold text-center">
              Thank you! We've received your quote request and will contact you soon.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-gray-50 p-10 rounded-lg" data-readdy-form id="stone-services-quote-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-bold text-black mb-2">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm"
                placeholder="John Smith"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-black mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm"
                placeholder="(828) 555-0123"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-bold text-black mb-2">
              Email Address *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm"
              placeholder="john@example.com"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-bold text-black mb-2">
              Job Address *
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm"
              placeholder="123 Main Street, Otto, NC 28763"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-bold text-black mb-2">
                Project Type *
              </label>
              <select
                name="projectType"
                value={formData.projectType}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm cursor-pointer"
              >
                <option value="">Select project type</option>
                <option value="Retaining Wall">Retaining Wall</option>
                <option value="Patio">Patio</option>
                <option value="Walkway">Walkway</option>
                <option value="Stone Veneer">Stone Veneer</option>
                <option value="Stone Steps">Stone Steps</option>
                <option value="Decorative Landscaping">Decorative Landscaping</option>
                <option value="Custom Design">Custom Design</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-black mb-2">
                Estimated Square Footage
              </label>
              <input
                type="text"
                name="squareFootage"
                value={formData.squareFootage}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm"
                placeholder="e.g., 200 sq ft"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-bold text-black mb-2">
              Preferred Stone Type
            </label>
            <input
              type="text"
              name="stoneType"
              value={formData.stoneType}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm"
              placeholder="e.g., Natural fieldstone, Flagstone, River rock"
            />
          </div>

          <div className="mb-8">
            <label className="block text-sm font-bold text-black mb-2">
              Additional Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={5}
              maxLength={500}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm resize-none"
              placeholder="Tell us more about your project, timeline, or any specific requirements..."
            ></textarea>
            <p className="text-sm text-gray-500 mt-2">
              {formData.notes.length}/500 characters
            </p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-yellow-500 text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? 'SUBMITTING...' : 'SUBMIT QUOTE REQUEST'}
          </button>
        </form>

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Prefer to speak with us directly?
          </p>
          <a
            href="tel:8285571234"
            className="inline-flex items-center gap-3 bg-black text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-line text-2xl"></i>
            CALL (828) 557-1234
          </a>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;